import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import PartnerSettingsLayout from "@/layouts/partner/settings-layout"
import PartnerLayout from "@/layouts/partner-layout"
import { cn, formatCurrency } from "@/lib/utils"
import { partnerSettingsPricingPath } from "@/routes"

interface AgentPrice {
  uid: string
  name: string
  wholesale_price_cents: number
  markup_override_kind: OverrideType | null
  markup_override_value: string | number | null
  calculated_retail_price_cents: number
}

type OverrideType = "percentage" | "fixed"

interface AgentRow {
  uid: string
  name: string
  wholesaleCents: number
  overrideType: OverrideType
  overrideValue: string
}

const ROUNDING = 500 // $5

// Mirrors PartnerAgentPrice#retail_price_cents: an override is exact, the
// default markup rounds up to the nearest $5.
function calcRetail(
  wholesaleCents: number,
  defaultMarkup: number,
  overrideType: OverrideType,
  overrideValue: string,
): number {
  const val = parseFloat(overrideValue)
  if (isNaN(val) || overrideValue === "") {
    const marked = wholesaleCents * (1 + defaultMarkup / 100)
    return Math.ceil(marked / ROUNDING) * ROUNDING
  }
  if (overrideType === "percentage") {
    return Math.round(wholesaleCents * (1 + val / 100))
  }
  // fixed markup amount added on top of wholesale
  return wholesaleCents + Math.round(val * 100)
}

export default function PricingSettings({
  partner,
  agent_prices,
}: {
  partner: {
    uid: string
    default_markup_percentage: number
  }
  agent_prices: AgentPrice[]
}) {
  const [markupPct, setMarkupPct] = useState(
    Number(partner.default_markup_percentage) || 0,
  )

  const [rows, setRows] = useState<AgentRow[]>(() =>
    agent_prices.map((ap) => ({
      uid: ap.uid,
      name: ap.name,
      wholesaleCents: ap.wholesale_price_cents,
      overrideType: ap.markup_override_kind ?? "percentage",
      overrideValue:
        ap.markup_override_value != null
          ? String(Number(ap.markup_override_value))
          : "",
    })),
  )

  const [processing, setProcessing] = useState(false)

  function updateRow(uid: string, patch: Partial<AgentRow>) {
    setRows((prev) => prev.map((r) => (r.uid === uid ? { ...r, ...patch } : r)))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    router.patch(
      partnerSettingsPricingPath(),
      {
        default_markup_percentage: markupPct,
        overrides: rows.map((r) => ({
          uid: r.uid,
          kind: r.overrideType,
          value: r.overrideValue,
        })),
      },
      { onFinish: () => setProcessing(false) },
    )
  }

  const wholesaleExample = 140

  return (
    <PartnerLayout>
      <Head title="Settings — Pricing" />
      <PartnerSettingsLayout>
        <div className="flex flex-col gap-10">
          {/* Global markup */}
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-8 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex flex-col gap-4">
                  <div>
                    <h2 className="text-sm font-semibold">Default markup</h2>
                    <p className="text-muted-foreground mt-1 text-sm">
                      Applied to all agents unless overridden individually.
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <div className="w-32">
                      <Input
                        id="markup"
                        className="text-sm tabular-nums"
                        suffix="%"
                        value={markupPct}
                        onChange={(e) => {
                          const raw = parseFloat(
                            e.target.value.replace(/[^0-9.]/g, ""),
                          )
                          setMarkupPct(isNaN(raw) ? 0 : raw)
                        }}
                      />
                    </div>
                    <p className="text-muted-foreground text-sm">
                      Prices round to the nearest $5 increment
                    </p>
                  </div>
                </div>

                <div className="bg-muted w-full space-y-3 rounded-lg border px-4 py-3 text-sm sm:max-w-md">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Wholesale cost
                    </span>
                    <span>${wholesaleExample}/mo per agent</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Your markup ({markupPct.toFixed(0)}%)
                    </span>
                    <span className="text-green-600 dark:text-green-500">
                      +$
                      {Math.ceil(
                        (wholesaleExample * (1 + markupPct / 100)) / 5,
                      ) *
                        5 -
                        wholesaleExample}
                      /mo
                    </span>
                  </div>
                  <div className="mt-1 flex justify-between border-t pt-2 font-medium">
                    <span>Client price</span>
                    <span>
                      $
                      {Math.ceil(
                        (wholesaleExample * (1 + markupPct / 100)) / 5,
                      ) * 5}
                      /mo per agent
                    </span>
                  </div>
                </div>
              </div>

              {/* Per-agent overrides */}
              {rows.length > 0 && (
                <div className="flex flex-col gap-3">
                  <div>
                    <h2 className="text-sm font-semibold">Per-agent markup</h2>
                    <p className="text-muted-foreground mt-1 text-sm">
                      Set a markup for individual agents. Leave blank to apply
                      the default.
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="text-foreground min-w-full text-left text-sm/6">
                      <thead className="text-muted-foreground">
                        <tr>
                          <th className="border-b py-2 pr-4 pl-0 font-medium">
                            Agent
                          </th>
                          <th className="border-b px-4 py-2 font-medium">
                            Wholesale
                          </th>
                          <th className="border-b px-4 py-2 font-medium">
                            Markup
                          </th>
                          <th className="border-b px-4 py-2 text-right font-medium">
                            Client price
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => {
                          const retail = calcRetail(
                            row.wholesaleCents,
                            markupPct,
                            row.overrideType,
                            row.overrideValue,
                          )
                          const isOverridden = row.overrideValue !== ""
                          return (
                            <tr key={row.uid}>
                              <td className="border-b py-3 pr-4 pl-0 font-medium">
                                {row.name}
                              </td>
                              <td className="text-muted-foreground border-b px-4 py-3 tabular-nums">
                                {formatCurrency(row.wholesaleCents / 100)}/mo
                              </td>
                              <td className="border-b px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <div className="inline-flex gap-1 rounded-lg bg-neutral-100 p-1 dark:bg-neutral-800">
                                    {(
                                      ["percentage", "fixed"] as OverrideType[]
                                    ).map((type) => (
                                      <button
                                        key={type}
                                        type="button"
                                        onClick={() =>
                                          updateRow(row.uid, {
                                            overrideType: type,
                                          })
                                        }
                                        className={cn(
                                          "flex h-7 w-7 items-center justify-center rounded-md text-sm transition-colors",
                                          row.overrideType === type
                                            ? "bg-white shadow-xs dark:bg-neutral-700 dark:text-neutral-100"
                                            : "text-neutral-500 hover:bg-neutral-200/60 hover:text-black dark:text-neutral-400 dark:hover:bg-neutral-700/60",
                                        )}
                                      >
                                        {type === "percentage" ? "%" : "$"}
                                      </button>
                                    ))}
                                  </div>
                                  <Input
                                    className="w-24 text-sm tabular-nums"
                                    value={row.overrideValue}
                                    onChange={(e) =>
                                      updateRow(row.uid, {
                                        overrideValue: e.target.value.replace(
                                          /[^0-9.]/g,
                                          "",
                                        ),
                                      })
                                    }
                                  />
                                </div>
                              </td>
                              <td className="border-b px-4 py-3 text-right tabular-nums">
                                <span
                                  className={
                                    isOverridden
                                      ? "font-medium"
                                      : "text-muted-foreground"
                                  }
                                >
                                  {formatCurrency(retail / 100)}/mo
                                </span>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button size="sm" className="rounded-sm" loading={processing}>
                  Save changes
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-sm"
                  onClick={() => router.reload()}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        </div>
      </PartnerSettingsLayout>
    </PartnerLayout>
  )
}
